import React, {
    useContext, useMemo 
} from 'react';
import {
    Box, Slide, 
    Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import {
    ChallengeFormContextValue,
    FormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, 
    TypographyWeights
} from '../../../../figmaTheme';
import {
    DiagnosticAnswers 
} from '../../config/types';

export const ChallengeConfirmation = (): JSX.Element => {
    const formContext = useContext(FormContext);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === 'challenge');
    }, [formContext?.sections]);
    const useStyles = makeStyles(() => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        imageContainer: {
            height: '64px',
            marginBottom: '32px',
            marginTop: '100px'
        },
        subtitle: {
            textAlign: 'center',
            marginBottom: '64px'
        },
        tipBox: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: section?.colors.background,
            padding: '16px',
            borderRadius: '8px',
        }
    }));
    const classes = useStyles();

    const onClick = () => {
        if ((formContext as ChallengeFormContextValue)?.completeChallengeFlow) {
            (formContext as ChallengeFormContextValue)?.completeChallengeFlow();
        } else {
            formContext?.goNext();
        }
    };
    const subtitle = useMemo(() => {
        if (section?.challengeOptions.length > 1) {
            return `Sounds good! You've joined the ${section?.challengeName} <span style="color:${section?.colors.main};font-weight:${TypographyWeights.BOLD}">${(formContext.answers as DiagnosticAnswers)?.[`${section?.challengeIdentifier}-challenge_identifier`]} Challenge.</span>`;
        } else {
            return 'Awesome, you\'re in!';
        }
    }, [section?.challengeOptions, section?.challengeName, section?.colors.main, (formContext.answers as DiagnosticAnswers)?.[`${section?.challengeIdentifier}-challenge_identifier`]]);
    const icon = useMemo(() => {
        return section?.challengeOptions.find(challenge => challenge.key === (formContext.answers as DiagnosticAnswers)?.[`${section?.challengeIdentifier}-challenge_identifier`])?.icon;
    }, [section?.challengeOptions, (formContext.answers as DiagnosticAnswers)?.[`${section?.challengeIdentifier}-challenge_identifier`]]);

    return (
        <CardContainer
            sectionKey={'challenge'}
            headerText={'Set Up Your Challenge'}
            goBack={formContext?.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Box className={classes.imageContainer}>
                                <Box
                                    style={{
                                        transform: 'scale(2)',
                                        transformOrigin: 'top' 
                                    }}
                                >
                                    {icon}
                                </Box>
                            </Box>
                            <Typography
                                variant={TypographyVariants.H3}
                                className={classes.subtitle}
                                dangerouslySetInnerHTML={{
                                    __html: subtitle 
                                }}
                            />
                            <Box className={classes.tipBox}>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    dangerouslySetInnerHTML={{
                                        __html: `<span style="font-weight:${TypographyWeights.BOLD}">At Sunnyside, we're about progress, not perfection.</span> Do your best, and we'll support you towards a great month even if you have slip ups here and there.` 
                                    }}
                                />
                            </Box>
                            <Box
                                style={{
                                    height: '500px' 
                                }}
                            />
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default ChallengeConfirmation;
